import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { Plus, MoreHorizontal, Pencil, Trash2, Check, ChevronsUpDown, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Comprehensive list of worldwide timezones grouped by region
const TIMEZONES = [
  // Africa
  { value: "Africa/Cairo", label: "Cairo (EET)", region: "Africa" },
  { value: "Africa/Casablanca", label: "Casablanca (WET)", region: "Africa" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)", region: "Africa" },
  { value: "Africa/Lagos", label: "Lagos (WAT)", region: "Africa" },
  { value: "Africa/Nairobi", label: "Nairobi (EAT)", region: "Africa" },
  { value: "Africa/Tunis", label: "Tunis (CET)", region: "Africa" },
  
  // America
  { value: "America/Anchorage", label: "Anchorage (AKST)", region: "America" },
  { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires (ART)", region: "America" },
  { value: "America/Bogota", label: "Bogota (COT)", region: "America" },
  { value: "America/Chicago", label: "Chicago (CST)", region: "America" },
  { value: "America/Denver", label: "Denver (MST)", region: "America" },
  { value: "America/Lima", label: "Lima (PET)", region: "America" },
  { value: "America/Los_Angeles", label: "Los Angeles (PST)", region: "America" },
  { value: "America/Mexico_City", label: "Mexico City (CST)", region: "America" },
  { value: "America/New_York", label: "New York (EST)", region: "America" },
  { value: "America/Phoenix", label: "Phoenix (MST)", region: "America" },
  { value: "America/Santiago", label: "Santiago (CLT)", region: "America" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)", region: "America" },
  { value: "America/Toronto", label: "Toronto (EST)", region: "America" },
  { value: "America/Vancouver", label: "Vancouver (PST)", region: "America" },
  
  // Asia
  { value: "Asia/Bangkok", label: "Bangkok (ICT)", region: "Asia" },
  { value: "Asia/Dhaka", label: "Dhaka (BST)", region: "Asia" },
  { value: "Asia/Dubai", label: "Dubai (GST)", region: "Asia" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)", region: "Asia" },
  { value: "Asia/Istanbul", label: "Istanbul (TRT)", region: "Asia" },
  { value: "Asia/Jakarta", label: "Jakarta (WIB)", region: "Asia" },
  { value: "Asia/Jerusalem", label: "Jerusalem (IST)", region: "Asia" },
  { value: "Asia/Karachi", label: "Karachi (PKT)", region: "Asia" },
  { value: "Asia/Kolkata", label: "Kolkata (IST)", region: "Asia" },
  { value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur (MYT)", region: "Asia" },
  { value: "Asia/Manila", label: "Manila (PHT)", region: "Asia" },
  { value: "Asia/Riyadh", label: "Riyadh (AST)", region: "Asia" },
  { value: "Asia/Seoul", label: "Seoul (KST)", region: "Asia" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)", region: "Asia" },
  { value: "Asia/Singapore", label: "Singapore (SGT)", region: "Asia" },
  { value: "Asia/Taipei", label: "Taipei (CST)", region: "Asia" },
  { value: "Asia/Tehran", label: "Tehran (IRST)", region: "Asia" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)", region: "Asia" },
  
  // Australia & Pacific
  { value: "Australia/Melbourne", label: "Melbourne (AEST)", region: "Australia/Pacific" },
  { value: "Australia/Perth", label: "Perth (AWST)", region: "Australia/Pacific" },
  { value: "Australia/Sydney", label: "Sydney (AEST)", region: "Australia/Pacific" },
  { value: "Pacific/Auckland", label: "Auckland (NZST)", region: "Australia/Pacific" },
  { value: "Pacific/Fiji", label: "Fiji (FJT)", region: "Australia/Pacific" },
  { value: "Pacific/Honolulu", label: "Honolulu (HST)", region: "Australia/Pacific" },
  
  // Europe
  { value: "Europe/Amsterdam", label: "Amsterdam (CET)", region: "Europe" },
  { value: "Europe/Athens", label: "Athens (EET)", region: "Europe" },
  { value: "Europe/Berlin", label: "Berlin (CET)", region: "Europe" },
  { value: "Europe/Brussels", label: "Brussels (CET)", region: "Europe" },
  { value: "Europe/Budapest", label: "Budapest (CET)", region: "Europe" },
  { value: "Europe/Copenhagen", label: "Copenhagen (CET)", region: "Europe" },
  { value: "Europe/Dublin", label: "Dublin (GMT)", region: "Europe" },
  { value: "Europe/Helsinki", label: "Helsinki (EET)", region: "Europe" },
  { value: "Europe/Lisbon", label: "Lisbon (WET)", region: "Europe" },
  { value: "Europe/London", label: "London (GMT)", region: "Europe" },
  { value: "Europe/Madrid", label: "Madrid (CET)", region: "Europe" },
  { value: "Europe/Moscow", label: "Moscow (MSK)", region: "Europe" },
  { value: "Europe/Oslo", label: "Oslo (CET)", region: "Europe" },
  { value: "Europe/Paris", label: "Paris (CET)", region: "Europe" },
  { value: "Europe/Prague", label: "Prague (CET)", region: "Europe" },
  { value: "Europe/Rome", label: "Rome (CET)", region: "Europe" },
  { value: "Europe/Stockholm", label: "Stockholm (CET)", region: "Europe" },
  { value: "Europe/Vienna", label: "Vienna (CET)", region: "Europe" },
  { value: "Europe/Warsaw", label: "Warsaw (CET)", region: "Europe" },
  { value: "Europe/Zurich", label: "Zurich (CET)", region: "Europe" },
  
  // UTC
  { value: "UTC", label: "UTC (Coordinated Universal Time)", region: "UTC" },
];

// Group timezones by region
const groupedTimezones = TIMEZONES.reduce((acc, tz) => {
  if (!acc[tz.region]) {
    acc[tz.region] = [];
  }
  acc[tz.region].push(tz);
  return acc;
}, {});

const AdminCities = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    timezone: 'Europe/Rome',
  });

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const response = await adminAPI.listCities();
      setCities(response.data);
    } catch (error) {
      toast.error('Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCity) {
        await adminAPI.updateCity(editingCity.id, formData);
        toast.success('City updated successfully');
      } else {
        await adminAPI.createCity(formData);
        toast.success('City created successfully');
      }
      setDialogOpen(false);
      setEditingCity(null);
      setFormData({ name: '', country: '', timezone: 'Europe/Rome' });
      loadCities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save city');
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      country: city.country,
      timezone: city.timezone || 'Europe/Rome',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (cityId) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;
    try {
      await adminAPI.deleteCity(cityId);
      toast.success('City deleted');
      loadCities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete city');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingCity(null);
    setFormData({ name: '', country: '', timezone: 'Europe/Rome' });
  };

  const selectedTimezone = TIMEZONES.find(tz => tz.value === formData.timezone);

  return (
    <AdminLayout title="Cities" subtitle="Manage cities where monuments are located">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Cities</CardTitle>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            data-testid="create-city-btn"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add City
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cities.map((city) => (
                  <TableRow key={city.id} className="table-row-hover" data-testid={`city-row-${city.id}`}>
                    <TableCell className="font-medium">{city.name}</TableCell>
                    <TableCell>{city.country}</TableCell>
                    <TableCell className="text-slate-500">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        {city.timezone || 'Not set'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`city-actions-${city.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(city)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(city.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {cities.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No cities found. Add your first city to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit City Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingCity ? 'Edit City' : 'Add New City'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>City Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Rome"
                required
                data-testid="city-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="e.g., Italy"
                required
                data-testid="city-country-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Timezone
              </Label>
              <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={timezoneOpen}
                    className="w-full justify-between font-normal"
                    data-testid="city-timezone-select"
                  >
                    {selectedTimezone ? (
                      <span className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-indigo-600" />
                        {selectedTimezone.label}
                      </span>
                    ) : (
                      <span className="text-slate-500">Select timezone...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search timezone..." />
                    <CommandList>
                      <CommandEmpty>No timezone found.</CommandEmpty>
                      {Object.entries(groupedTimezones).map(([region, timezones]) => (
                        <CommandGroup key={region} heading={region}>
                          {timezones.map((tz) => (
                            <CommandItem
                              key={tz.value}
                              value={tz.label}
                              onSelect={() => {
                                setFormData({ ...formData, timezone: tz.value });
                                setTimezoneOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.timezone === tz.value ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {tz.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500">
                Selected: {formData.timezone}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-city-btn">
                {editingCity ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCities;
